import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { AccountProvider } from './contexts/AccountContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import { ThemeProvider } from './contexts/ThemeContext';
import FluentRoot from './components/FluentRoot';
import PrivateRoute from './components/PrivateRoute';
import { initGA, logPageView } from './services/analytics';

// Public pages (loaded immediately)
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import AcceptInvite from './pages/AcceptInvite';
import FacebookCallback from './pages/FacebookCallback';
import NotFound from './pages/NotFound';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookieBanner from './components/CookieBanner';

// Lazy load feature modules for better performance
const HomePage = lazy(() => import('./pages/HomePage'));
const Generate = lazy(() => import('./features/generate/GeneratePage'));
const Templates = lazy(() => import('./features/templates/TemplatesPage'));
const TemplateEditorPage = lazy(() => import('./features/templates/pages/TemplateEditorPage').then(module => ({ default: module.TemplateEditorPage })));
const Preview = lazy(() => import('./features/preview/PreviewPage'));
const Publish = lazy(() => import('./features/publish/PublishPage'));
const Library = lazy(() => import('./features/library/LibraryPage'));
const Calendar = lazy(() => import('./features/calendar/CalendarPage'));
const Platforms = lazy(() => import('./features/platforms/PlatformsPage'));
const ProfileSettings = lazy(() => import('./features/settings/ProfileSettings'));
const WorkspaceSettingsPage = lazy(() => import('./features/settings/WorkspaceSettingsPage'));
const AccountSettings = lazy(() => import('./features/settings/AccountSettings'));
const BillingSettings = lazy(() => import('./features/settings/BillingSettings'));
const TeamSettings = lazy(() => import('./features/settings/TeamSettings'));
const Onboarding = lazy(() => import('./features/onboarding/OnboardingPage'));
const Analytics = lazy(() => import('./features/analytics/AnalyticsPage'));

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

// Analytics tracking component
function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    logPageView(location.pathname, document.title);
  }, [location]);

  return null;
}

function App() {
  useEffect(() => {
    // Initialize Google Analytics on app load
    initGA();
  }, []);

  return (
    <Router>
      <AuthProvider>
        <AccountProvider>
          <LanguageProvider>
            <WorkspaceProvider>
              <ThemeProvider>
                <FluentRoot>
                <div className="App">
                  <Toaster position="top-right" />
                  <CookieBanner />
                  <AnalyticsTracker />
                  <Suspense fallback={<LoadingFallback />}>
                    <Routes>
              {/* Public routes */}
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/invite/:inviteToken" element={<AcceptInvite />} />
              <Route path="/oauth/facebook/callback" element={<FacebookCallback />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />

              {/* Protected routes - RewordGenie features */}
              <Route
                path="/onboarding"
                element={
                  <PrivateRoute>
                    <Onboarding />
                  </PrivateRoute>
                }
              />
              <Route
                path="/home"
                element={
                  <PrivateRoute>
                    <HomePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/generate"
                element={
                  <PrivateRoute>
                    <Generate />
                  </PrivateRoute>
                }
              />
              <Route
                path="/templates"
                element={<Navigate to="/library/templates" replace />}
              />
              <Route
                path="/templates/edit"
                element={
                  <PrivateRoute>
                    <TemplateEditorPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/templates/edit/:id"
                element={
                  <PrivateRoute>
                    <TemplateEditorPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/preview"
                element={
                  <PrivateRoute>
                    <Preview />
                  </PrivateRoute>
                }
              />
              <Route
                path="/publish"
                element={
                  <PrivateRoute>
                    <Publish />
                  </PrivateRoute>
                }
              />
              <Route
                path="/library"
                element={
                  <PrivateRoute>
                    <Library />
                  </PrivateRoute>
                }
              />
              <Route
                path="/library/templates"
                element={
                  <PrivateRoute>
                    <Templates />
                  </PrivateRoute>
                }
              />
              <Route
                path="/calendar"
                element={
                  <PrivateRoute>
                    <Calendar />
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings/profile"
                element={
                  <PrivateRoute>
                    <ProfileSettings />
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings/workspace"
                element={
                  <PrivateRoute>
                    <WorkspaceSettingsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings/account"
                element={
                  <PrivateRoute>
                    <AccountSettings />
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings/billing"
                element={
                  <PrivateRoute>
                    <BillingSettings />
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings/team"
                element={
                  <PrivateRoute>
                    <TeamSettings />
                  </PrivateRoute>
                }
              />
              <Route
                path="/platforms"
                element={
                  <PrivateRoute>
                    <Platforms />
                  </PrivateRoute>
                }
              />
              <Route
                path="/analytics"
                element={
                  <PrivateRoute>
                    <Analytics />
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings"
                element={<Navigate to="/settings/profile" replace />}
              />

              {/* Error routes */}
              <Route path="/404" element={<NotFound />} />
              <Route path="*" element={<Navigate to="/404" replace />} />
                  </Routes>
                </Suspense>
              </div>
                </FluentRoot>
            </ThemeProvider>
            </WorkspaceProvider>
          </LanguageProvider>
        </AccountProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
